// frontend/src/components/onboarding/OnboardingWizard.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { api } from '@/services/api';
import { PersonaGrid } from '@/components/voice/PersonaGrid';
import { VOICE_ENABLED } from '@/lib/featureFlags';
import { usePersonaPreferences } from '@/hooks/usePersonaPreferences';
import { useCompanionName } from '@/hooks/useCompanionName';
import { cn, localizedApiErrorMessage } from '@/lib/utils';
import { NAV_KEY } from '@/i18n/keys/nav';
import { digitsOnly, FieldLabel, MonoLabel, numericInputProps } from '@/components/editorial';
import { Heart, ListChecks, ShieldCheck, AudioLines, Sparkles, X, CheckCircle2 } from 'lucide-react';
import type { FamilyMine } from '@/types/api';
import { onboardingWizardSteps, onboardingWorkingSteps, stepAfterSecurity, useNavPlacement, type NavPlacement } from './steps';

// Step 2, "Build your support circle", was retired with the People address
// book (wave w1023c, Victor 2026-10-23). The other steps keep their numbers,
// so the counter and the dots count positions, not numbers.
type Step = 1 | 3 | 4 | 5 | 6;
type MfaState = 'idle' | 'sending' | 'awaiting-code' | 'verifying' | 'success';

interface Props {
  family: FamilyMine;
  /** A resume saved before wave w1023c may still name the retired step 2;
   *  it opens the task step, the next one. */
  initialStep?: Step | 2;
  onClose: () => void;
  onStartGuide: () => void;
}

/** The step's mono eyebrow and serif title, as `onboarding` keys (#2109 R3-B4). */
interface StepMeta { stepKey: StepKey; Icon: typeof Heart }
type StepKey = 'patient' | 'firstRequest' | 'security' | 'voice' | 'done';

// Mono eyebrow + serif title per step. Test pins the title strings — the
// English catalog keeps them byte-identical.
// Step 5 names the companion, so its header must resolve the SAME family-scoped
// value the step body uses (`useCompanionName`). Templating the global
// COMPANION_NAME here put "Ember" in the heading over "Maya" in the paragraph —
// #639 recurring on a parent surface. One screen, one name: the header passes
// `companion` to both keys.
const STEP_META: Record<Step, StepMeta> = {
  1: { stepKey: 'patient', Icon: Heart },
  3: { stepKey: 'firstRequest', Icon: ListChecks },
  4: { stepKey: 'security', Icon: ShieldCheck },
  5: { stepKey: 'voice', Icon: AudioLines },
  6: { stepKey: 'done', Icon: Sparkles },
};

/** The treatment phases offered on step 1; each label is `onboarding:wizard.phase.<value>`. */
const TREATMENT_PHASE_VALUES = [
  'diagnosis',
  'active_treatment',
  'maintenance',
  'survivorship',
  'off_treatment',
  'other',
] as const;

// #2386: the header counts the working steps the home banner counts — four,
// or three while voice is off (ruling 2026-10-15; the circle step went in
// wave w1023c). Step 6, the closing
// "You're all set" card, is not a step anyone completes, so it carries no
// counter.

/**
 * #2387: the track is elastic. It takes whatever the row leaves after the
 * counter and the exit link, and each segment shrinks from 24 px down to
 * 4 px, so a wide exit label (zh "退出设置" at 390 px) is never struck through.
 */
function StepDots({ step }: { step: Step }) {
  return (
    <div data-testid="onboarding-step-track" className="flex flex-1 min-w-0 items-center gap-1">
      {onboardingWizardSteps().map((s) => (
        <span
          key={s}
          className={cn(
            'h-[3px] flex-1 min-w-1 max-w-6 rounded-full transition-colors',
            s === step ? 'bg-stevens-indigo' : s < step ? 'bg-stevens-indigo/50' : 'bg-muted',
          )}
        />
      ))}
    </div>
  );
}

function StepHeader({ step, companionName }: { step: Step; companionName: string }) {
  const { t } = useTranslation('onboarding');
  const meta = STEP_META[step];
  const Icon = meta.Icon;
  return (
    <header className="flex items-start gap-3 mb-5">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-stevens-indigo/10 text-stevens-indigo flex-shrink-0">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <div className="flex-1 min-w-0">
        <MonoLabel className="block mb-1">
          {t(`wizard.steps.${meta.stepKey}.eyebrow`, { companion: companionName })}
        </MonoLabel>
        <h2
          id="onboarding-step-title"
          className="font-serif text-card-title font-normal leading-[1.25] tracking-tight"
        >
          {t(`wizard.steps.${meta.stepKey}.title`, { companion: companionName })}
        </h2>
      </div>
    </header>
  );
}

function PickerTile({ active, onClick, children, className }: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border px-3 py-2 text-caption transition-colors',
        active
          ? 'border-stevens-indigo bg-stevens-indigo/5 text-foreground'
          : 'border-border bg-card text-stevens-ink-3 hover:text-foreground hover:bg-muted',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function OnboardingWizard({ family, initialStep, onClose, onStartGuide }: Props) {
  const navigate = useNavigate();
  const companionName = useCompanionName();
  const { t, i18n } = useTranslation(['onboarding', 'nav']);
  const navPlacement = useNavPlacement();
  // R3-B11: a non-English reader never sees the server's English sentence.
  const apiError = (e: unknown) => localizedApiErrorMessage(e, t, i18n.resolvedLanguage ?? i18n.language);
  const [step, setStep] = useState<Step>(initialStep === 2 ? 3 : initialStep ?? 1);
  // The counter's "03 of 04": the step's position among the working steps.
  const position = onboardingWizardSteps().indexOf(step as never) + 1;
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Step 1 state
  const [childName, setChildName] = useState(family.patient.name || '');
  const [childDob, setChildDob] = useState(
    family.patient.date_of_birth ? family.patient.date_of_birth.slice(0, 10) : '',
  );
  const [diagnosis, setDiagnosis] = useState(family.patient.diagnosis || '');
  const [treatmentPhase, setTreatmentPhase] = useState(family.patient.treatment_phase || '');
  const [hospital, setHospital] = useState(family.patient.hospital || '');
  // Cycle tracking is opt-in — many families don't think in cycles, and
  // forcing the question would slow onboarding. We expand the section only
  // when the family already has cycle data or the parent clicks the toggle.
  const [showCycle, setShowCycle] = useState(family.patient.cycle_number != null);
  const [cycleNumber, setCycleNumber] = useState(
    family.patient.cycle_number != null ? String(family.patient.cycle_number) : '',
  );
  const [cycleStartedAt, setCycleStartedAt] = useState(
    family.patient.cycle_started_at ? family.patient.cycle_started_at.slice(0, 10) : '',
  );
  const [cycleLengthDays, setCycleLengthDays] = useState(
    family.patient.cycle_length_days != null ? String(family.patient.cycle_length_days) : '21',
  );

  // Step 4 state
  const [mfaState, setMfaState] = useState<MfaState>('idle');
  const [mfaCode, setMfaCode] = useState('');

  const shownAtMarked = useRef(false);

  // Mark shown_at on first mount
  useEffect(() => {
    if (!family.onboarding.shown_at && !shownAtMarked.current) {
      shownAtMarked.current = true;
      api.families.updateOnboarding({ shown_at: new Date().toISOString() }).catch(() => {});
    }
  }, [family.onboarding.shown_at]);

  // Suppress the standalone MFA modal — we handle MFA inline in Step 4
  useEffect(() => {
    localStorage.removeItem('mfa_onboarding_pending');
  }, []);

  // ── Step 1 ─────────────────────────────────────────────────────────────────

  async function handleStep1Continue() {
    setSubmitting(true);
    try {
      setError('');
      const cycleNum = showCycle && cycleNumber ? parseInt(cycleNumber, 10) : null;
      const cycleLen = showCycle && cycleLengthDays ? parseInt(cycleLengthDays, 10) : null;
      const hasAny =
        childName || childDob || diagnosis || treatmentPhase || hospital ||
        (showCycle && (cycleNum || cycleStartedAt));
      if (hasAny) {
        await api.families.updatePatient({
          name: childName || undefined,
          date_of_birth: childDob || undefined,
          diagnosis: diagnosis || undefined,
          treatment_phase: treatmentPhase || undefined,
          hospital: hospital || undefined,
          ...(showCycle && {
            cycle_number: Number.isFinite(cycleNum) ? cycleNum : null,
            cycle_started_at: cycleStartedAt || null,
            cycle_length_days: Number.isFinite(cycleLen) ? cycleLen : null,
          }),
        });
      }
      await api.families.updateOnboarding({ steps: { patient_info: { done: true } } });
      setStep(3);
    } catch (e) {
      setError(apiError(e) || t('wizard.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStep1Skip() {
    setError('');
    try {
      await api.families.updateOnboarding({ steps: { patient_info: { skipped: true } } });
      setStep(3);
    } catch (e) {
      setError(apiError(e) || t('wizard.errors.generic'));
    }
  }

  // ── Step 3 — Task guide ────────────────────────────────────────────────────

  function handleStep3GotIt() {
    onStartGuide();
    onClose();
  }

  async function handleStep3Skip() {
    setError('');
    try {
      await api.families.updateOnboarding({ steps: { first_task: { skipped: true } } });
      setStep(4);
    } catch (e) {
      setError(apiError(e) || t('wizard.errors.generic'));
    }
  }

  // ── Step 4 — MFA ───────────────────────────────────────────────────────────

  async function handleStep4Enable() {
    setMfaState('sending');
    setError('');
    try {
      await api.mfa.enrollStart('email');
      setMfaCode('');
      setMfaState('awaiting-code');
    } catch (e) {
      setMfaState('idle');
      setError(apiError(e) || t('wizard.errors.sendCode'));
    }
  }

  async function handleStep4Verify() {
    if (mfaCode.length !== 6) return;
    setMfaState('verifying');
    setError('');
    try {
      await api.mfa.enrollVerify('email', mfaCode);
      setMfaState('success');
      setTimeout(() => setStep(stepAfterSecurity()), 1500);
    } catch (e) {
      setMfaState('awaiting-code');
      setError(apiError(e) || t('wizard.errors.invalidCode'));
    }
  }

  // Ruling 2026-10-15 (Victor): "Maybe later" settles the step, so the home
  // banner's Continue moves past it instead of sending the family back.
  async function handleStep4Skip() {
    setError('');
    try {
      await api.families.updateOnboarding({ steps: { security: { skipped: true } } });
    } catch {
      // best-effort — don't block progression, as on the voice step
    }
    setStep(stepAfterSecurity());
  }

  // ── Step 5 — Voice ─────────────────────────────────────────────────────────

  async function handleStep5Continue() {
    setError('');
    try {
      await api.families.updateOnboarding({ steps: { voice_chosen: { done: true } } });
    } catch {
      // best-effort — don't block progression to the done card
    }
    setStep(6);
  }

  async function handleStep5Skip() {
    setError('');
    try {
      await api.families.updateOnboarding({ steps: { voice_chosen: { skipped: true } } });
    } catch {
      // best-effort
    }
    setStep(6);
  }

  // ── Step 6 — Done ──────────────────────────────────────────────────────────

  async function handleDoneAndNavigate() {
    try {
      setError('');
      setSubmitting(true);
      await api.families.updateOnboarding({ completed: true });
      navigate('/chat');
      onClose();
    } catch (e) {
      setError(apiError(e) || t('wizard.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDone() {
    try {
      setError('');
      await api.families.updateOnboarding({ completed: true });
      onClose();
    } catch (e) {
      setError(apiError(e) || t('wizard.errors.generic'));
    }
  }

  function handleSkipSetupConfirm() {
    setShowSkipConfirm(false);
    onClose();
  }

  const defaultStep = { done: false, skipped: false };
  const raw = family.onboarding?.steps;
  const steps = {
    patient_info:  raw?.patient_info  ?? defaultStep,
    first_task:    raw?.first_task    ?? defaultStep,
  };

  // #2616: the Radix dialog primitive, so focus stays inside the wizard and
  // the app behind the backdrop is hidden from screen readers and pointer
  // input while it is open. The overlay is the old backdrop (the scrollable-
  // overlay pattern keeps the card centred exactly as before). Escape and a
  // click on the backdrop still do nothing: the wizard closes only through
  // its own exit confirmation or its last step, as it always has.
  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'var(--stevens-scrim-soft)' }}
        >
      {/* Modal card */}
      <DialogPrimitive.Content
        aria-modal="true"
        // The step's own h2 names the dialog; e2e and the tests find it by this id.
        aria-labelledby="onboarding-step-title"
        aria-describedby={undefined}
        // Initial focus on the card itself, so a screen reader starts at the
        // step title rather than at the exit link.
        tabIndex={-1}
        onOpenAutoFocus={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement | null)?.focus(); }}
        onEscapeKeyDown={(e) => { e.preventDefault(); setShowSkipConfirm(true); }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="relative w-full max-w-md rounded-2xl bg-card border shadow-xl flex flex-col focus:outline-none"
      >
        {/* Radix requires a Title in the tree; the visible step h2 (which
            aria-labelledby points at) is the real name, so this one is hidden. */}
        <DialogPrimitive.Title hidden />
        {/* Top strip — mono step counter on the left, step dots in the middle, exit on the right */}
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-4 border-b">
          <div className="flex flex-1 items-center gap-3 min-w-0">
            {position > 0 && (
              <MonoLabel className="whitespace-nowrap">
                <Trans
                  t={t}
                  i18nKey="wizard.stepCounter"
                  values={{ step: String(position).padStart(2, '0'), total: String(onboardingWorkingSteps()).padStart(2, '0') }}
                  components={{ muted: <span className="text-stevens-ink-3/60" /> }}
                />
              </MonoLabel>
            )}
            <StepDots step={step} />
          </div>
          <button
            type="button"
            className="text-stevens-ink-3 hover:text-foreground transition-colors shrink-0 whitespace-nowrap"
            onClick={() => setShowSkipConfirm(true)}
          >
            <MonoLabel className="text-inherit">{t('wizard.exitLink')}</MonoLabel>
          </button>
        </div>

        {/* Exit confirmation overlay */}
        {showSkipConfirm && (
          <div className="absolute inset-0 z-10 rounded-2xl flex flex-col items-center justify-center gap-4 p-8 bg-card/95 backdrop-blur-sm">
            <p
              className="font-serif text-card-title font-normal leading-snug text-center"
            >
              {t('wizard.exit.title')}
            </p>
            <p className="text-body text-stevens-ink-3 text-center max-w-[24ch]">
              {t('wizard.exit.body')}
            </p>
            <div className="flex flex-col gap-2 w-full max-w-[260px] mt-2">
              <Button
                variant="brand" className="w-full"
                onClick={handleSkipSetupConfirm}
              >
                {t('wizard.exit.confirm')}
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => setShowSkipConfirm(false)}>
                {t('wizard.exit.back')}
              </Button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-5 max-h-[75vh] overflow-y-auto">
          {/* Step 1 — Patient Info */}
          {step === 1 && (
            <>
              <StepHeader step={1} companionName={companionName} />
              <p
                className="text-body text-foreground/75 leading-relaxed mb-5"
              >
                {t('wizard.patient.intro', { companion: companionName })}
              </p>
              <div className="space-y-4 mb-5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel className="mb-1.5">{t('wizard.patient.childName')}</FieldLabel>
                    <Input
                      className="h-9 bg-background"
                      placeholder={t('wizard.patient.childName')}
                      value={childName}
                      onChange={(e) => setChildName(e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel className="mb-1.5">{t('wizard.patient.dateOfBirth')}</FieldLabel>
                    <Input
                      type="date"
                      className="h-9 bg-background"
                      value={childDob}
                      onChange={(e) => setChildDob(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <FieldLabel className="mb-1.5">{t('wizard.patient.diagnosis')}</FieldLabel>
                  <Input
                    className="h-9 bg-background"
                    placeholder={t('wizard.patient.diagnosisPlaceholder')}
                    value={diagnosis}
                    onChange={(e) => setDiagnosis(e.target.value)}
                  />
                </div>
                <div>
                  <FieldLabel className="mb-1.5">{t('wizard.patient.treatmentPhase')}</FieldLabel>
                  <div className="flex flex-wrap gap-1.5">
                    {TREATMENT_PHASE_VALUES.map((value) => (
                      <PickerTile
                        key={value}
                        active={treatmentPhase === value}
                        onClick={() => setTreatmentPhase(treatmentPhase === value ? '' : value)}
                      >
                        {t(`wizard.phase.${value}`)}
                      </PickerTile>
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel className="mb-1.5">{t('wizard.patient.hospital')}</FieldLabel>
                  <Input
                    className="h-9 bg-background"
                    placeholder={t('wizard.patient.hospital')}
                    value={hospital}
                    onChange={(e) => setHospital(e.target.value)}
                  />
                </div>

                <div className="rounded-lg border border-dashed bg-muted/30 px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => setShowCycle((v) => !v)}
                    className="flex w-full items-center justify-between text-caption text-stevens-ink-3 hover:text-foreground transition-colors"
                  >
                    <span className="font-medium">{t('wizard.patient.cycleToggle')}</span>
                    <span className="text-stevens-ink-3/70">{showCycle ? '−' : '+'}</span>
                  </button>
                  {showCycle && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div>
                        <FieldLabel className="mb-1.5">{t('wizard.patient.cycleNumber')}</FieldLabel>
                        <Input
                          {...numericInputProps({ max: 99 })}
                          className="h-9 bg-background"
                          placeholder="3"
                          value={cycleNumber}
                          onChange={(e) => setCycleNumber(digitsOnly(e.target.value))}
                        />
                      </div>
                      <div>
                        <FieldLabel className="mb-1.5">{t('wizard.patient.cycleStarted')}</FieldLabel>
                        <Input
                          type="date"
                          className="h-9 bg-background"
                          value={cycleStartedAt}
                          onChange={(e) => setCycleStartedAt(e.target.value)}
                        />
                      </div>
                      <div>
                        <FieldLabel className="mb-1.5">{t('wizard.patient.cycleLength')}</FieldLabel>
                        <Input
                          {...numericInputProps({ max: 60 })}
                          className="h-9 bg-background"
                          placeholder="21"
                          value={cycleLengthDays}
                          onChange={(e) => setCycleLengthDays(digitsOnly(e.target.value))}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="brand" className="w-full"
                disabled={submitting}
                onClick={handleStep1Continue}
              >
                {t('wizard.actions.continue')}
              </Button>
              <SkipLink onClick={handleStep1Skip} />
            </>
          )}

          {/* Step 3 — First Task (guided) */}
          {step === 3 && (
            <>
              <StepHeader step={3} companionName={companionName} />
              <p
                className="text-body text-foreground/75 leading-relaxed mb-4"
              >
                {t('wizard.firstTask.intro', { nav: t(NAV_KEY.tasks) })}
              </p>

              <ol className="space-y-2 mb-4">
                <GuideRow num={1} title={t('wizard.firstTask.openNav', { nav: t(NAV_KEY.tasks) })}>
                  <OpenNavSentence navKey={NAV_KEY.tasks} placement={navPlacement} />
                </GuideRow>
                {/* #649: nobody is notified. Promise what happens: the
                    request lands on the task list. */}
                <GuideRow num={2} title={t('wizard.firstTask.requestTitle')}>
                  {t('wizard.firstTask.requestBody')}
                </GuideRow>
              </ol>

              <div className="rounded-lg border border-stevens-indigo/15 bg-stevens-indigo/5 px-3.5 py-3 mb-5">
                <MonoLabel tone="indigo" className="block mb-1">
                  {t('wizard.worthKnowing')}
                </MonoLabel>
                <p className="text-body-sm text-foreground/85 leading-relaxed">
                  <Trans
                    t={t}
                    i18nKey="wizard.firstTask.note"
                    values={{ nav: t(NAV_KEY.calendar) }}
                    components={{ strong: <strong /> }}
                  />
                </p>
              </div>

              <Button
                variant="brand" className="w-full"
                onClick={handleStep3GotIt}
              >
                {t('wizard.actions.gotIt')}
              </Button>
              <SkipLink onClick={handleStep3Skip} />
            </>
          )}

          {/* Step 4 — MFA */}
          {step === 4 && (
            <>
              <StepHeader step={4} companionName={companionName} />
              {mfaState === 'success' ? (
                <div className="flex flex-col items-center gap-2 py-6 text-center">
                  <CheckCircle2 className="h-9 w-9 text-status-ok-fg" strokeWidth={1.5} />
                  <p className="font-serif text-card-title font-normal leading-snug">
                    {t('wizard.mfa.successTitle')}
                  </p>
                  <p className="text-body-sm text-stevens-ink-3">
                    {t('wizard.mfa.successBody')}
                  </p>
                </div>
              ) : mfaState === 'awaiting-code' || mfaState === 'verifying' ? (
                <>
                  <p className="text-body text-foreground/75 leading-relaxed mb-4">
                    {t('wizard.mfa.codeSent')}
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                    className="w-full rounded-md border px-3 py-2 font-mono text-stat-sm tracking-[0.4em] text-center mb-4 focus:outline-none focus:ring-2 focus:ring-stevens-indigo/40"
                  />
                  <Button
                    variant="brand" className="w-full"
                    disabled={mfaState === 'verifying' || mfaCode.length !== 6}
                    onClick={handleStep4Verify}
                  >
                    {mfaState === 'verifying' ? t('wizard.mfa.verifying') : t('wizard.mfa.verify')}
                  </Button>
                  <SkipLink onClick={handleStep4Skip} label={t('wizard.actions.maybeLater')} />
                </>
              ) : (
                <>
                  <p className="text-body text-foreground/75 leading-relaxed mb-4">
                    {t('wizard.mfa.intro')}
                  </p>
                  <div className="rounded-lg border border-stevens-indigo/15 bg-stevens-indigo/5 px-3.5 py-3 mb-5">
                    <MonoLabel tone="indigo" className="block mb-1">
                      {t('wizard.mfa.howItWorks')}
                    </MonoLabel>
                    <p className="text-body-sm text-foreground/85 leading-relaxed">
                      {t('wizard.mfa.howItWorksBody')}
                    </p>
                  </div>
                  <Button
                    variant="brand" className="w-full"
                    disabled={mfaState === 'sending'}
                    onClick={handleStep4Enable}
                  >
                    {mfaState === 'sending' ? t('wizard.mfa.sending') : t('wizard.mfa.enable')}
                  </Button>
                  <SkipLink onClick={handleStep4Skip} label={t('wizard.actions.maybeLater')} />
                </>
              )}
            </>
          )}

          {/* Step 5 — Voice */}
          {step === 5 && (
            <OnboardingVoiceStep
              companionName={companionName}
              onContinue={handleStep5Continue}
              onSkip={handleStep5Skip}
            />
          )}

          {/* Step 6 — Done */}
          {step === 6 && (
            <>
              <StepHeader step={6} companionName={companionName} />
              <p
                className="text-body text-foreground/75 leading-relaxed mb-4"
              >
                {t('wizard.done.intro')}
              </p>
              {(steps.patient_info?.done || steps.first_task?.done) && (
                <ul className="text-body-sm text-foreground/85 mb-4 space-y-1">
                  {steps.patient_info?.done && (
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-stevens-indigo" strokeWidth={1.75} />
                      {t('wizard.done.patientAdded')}
                    </li>
                  )}
                  {steps.first_task?.done && (
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-stevens-indigo" strokeWidth={1.75} />
                      {t('wizard.done.taskCreated')}
                    </li>
                  )}
                </ul>
              )}
              <div className="rounded-lg border border-stevens-indigo/15 bg-stevens-indigo/5 px-3.5 py-3 mb-5">
                <MonoLabel tone="indigo" className="block mb-1">
                  {t('wizard.done.about', { companion: companionName })}
                </MonoLabel>
                <p className="text-body-sm text-foreground/85 leading-relaxed">
                  {t('wizard.done.aboutBody', { companion: companionName })}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-stevens-indigo text-stevens-indigo hover:bg-stevens-indigo/5"
                  disabled={submitting}
                  onClick={handleDoneAndNavigate}
                >
                  {t('wizard.done.chat', { companion: companionName })}
                </Button>
                <Button
                  variant="brand" className="flex-1"
                  onClick={handleDone}
                >
                  {t('wizard.done.goToDashboard')}
                </Button>
              </div>
            </>
          )}

          {error && (
            <p className="mt-3 text-caption text-[oklch(55%_0.18_25)] text-center">{error}</p>
          )}
        </div>
      </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ── Small leaf helpers ───────────────────────────────────────────────────────

function SkipLink({ onClick, label }: { onClick: () => void; label?: string }) {
  const { t } = useTranslation('onboarding');
  return (
    <button
      type="button"
      className="mt-2.5 text-stevens-ink-3 hover:text-foreground transition-colors flex items-center gap-1.5"
      onClick={onClick}
    >
      <X className="h-3 w-3" strokeWidth={1.75} />
      <MonoLabel className="text-inherit">{label ?? t('wizard.actions.skip')}</MonoLabel>
    </button>
  );
}

/**
 * "Click X in the sidebar on the left" is only true at md and up (#2385). A
 * phone has no sidebar: its nav sits behind the bottom bar's More button, and
 * between sm and md behind the header's menu button.
 */
function OpenNavSentence({ navKey, placement }: { navKey: typeof NAV_KEY.support | typeof NAV_KEY.tasks; placement: NavPlacement }) {
  const { t } = useTranslation(['onboarding', 'nav']);
  const nav = t(navKey);
  const strong = { strong: <strong /> };
  if (placement === 'bottomMore') {
    return <Trans t={t} i18nKey="wizard.openMenuPhone" values={{ nav, more: t(NAV_KEY.tabMore) }} components={strong} />;
  }
  if (placement === 'headerMenu') {
    return <Trans t={t} i18nKey="wizard.openMenuTablet" values={{ nav }} components={strong} />;
  }
  return <Trans t={t} i18nKey="wizard.clickInSidebar" values={{ nav }} components={strong} />;
}

function GuideRow({ num, title, children }: { num: number; title: string; children: React.ReactNode }) {
  return (
    <Card asChild className="shadow-none">
      <li className="flex items-start gap-3 px-3.5 py-2.5">
        <span className="font-mono text-caption font-medium text-stevens-indigo tabular-nums flex-shrink-0 leading-[1.4]">
          {String(num).padStart(2, '0')}
        </span>
        <div className="min-w-0">
          <p className="text-body-sm font-medium text-foreground leading-snug">{title}</p>
          <p className="text-caption text-stevens-ink-3 leading-relaxed mt-0.5">{children}</p>
        </div>
        {/* A `(?)` about this row's wording, beside it rather than inside its sentence. */}
      </li>
    </Card>
  );
}

// ── Inline voice step component ──────────────────────────────────────────────

function OnboardingVoiceStep({
  companionName,
  onContinue,
  onSkip,
}: {
  companionName: string;
  onContinue: () => void;
  onSkip: () => void;
}) {
  const { characters, preferences, updatePreferences, isLoading } = usePersonaPreferences();
  const { t } = useTranslation('onboarding');

  const handleSelect = useCallback((id: import('@/services/settingsService').PersonaId) => {
    updatePreferences({ persona: id });
  }, [updatePreferences]);

  return (
    <>
      <StepHeader step={5} companionName={companionName} />
      <p
        className="text-body text-foreground/75 leading-relaxed mb-4"
      >
        {t('wizard.voice.intro', { companion: companionName })}
      </p>

      {isLoading ? (
        <p className="text-caption text-stevens-ink-3 text-center py-6">{t('wizard.voice.loading')}</p>
      ) : (
        <div className="mb-4">
          <PersonaGrid
            characters={characters}
            selectedId={preferences?.persona}
            onSelect={handleSelect}
            // #2384: no voice preview while voice is off; the companion is still chosen here.
            showPreview={VOICE_ENABLED}
            compact
          />
        </div>
      )}

      <Button
        variant="brand" className="w-full"
        onClick={onContinue}
      >
        {t('wizard.actions.continue')}
      </Button>
      <SkipLink onClick={onSkip} />
    </>
  );
}
